import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, Trash2, Edit2, Plus, X, Save, Dumbbell, Apple, FileText, ExternalLink, Check, EyeOff, Eye } from 'lucide-react';
import { useAdmin } from '../../context/AdminContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const emptyForm = (category = 'training') => ({
  id: null,
  title: '',
  category,
  month: MONTHS[new Date().getMonth()],
  year: new Date().getFullYear(),
  pdf_url: '',
  tracker_url: '',
  description: '',
  is_active: true,
});

const AdminPrograms = () => {
  const { isAuthenticated, loading: authLoading } = useAdmin();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm('training'));
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (isAuthenticated) fetchAll();
  }, [isAuthenticated]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/programs`);
      setPrograms(res.data || []);
    } catch (e) {
      setMessage({ type: 'error', text: 'Could not load programs' });
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(null), 4500);
  };

  const handleStartCreate = (category) => {
    setForm(emptyForm(category));
    setShowForm(true);
  };

  const handleEdit = (program) => {
    setForm({
      id: program.id,
      title: program.title || '',
      category: program.category || 'training',
      month: program.month || MONTHS[new Date().getMonth()],
      year: program.year || new Date().getFullYear(),
      pdf_url: program.pdf_url || '',
      tracker_url: program.tracker_url || '',
      description: program.description || '',
      is_active: program.is_active !== false,
    });
    setShowForm(true);
  };

  const handleUploadPdf = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      showMsg({ type: 'error', text: 'Only PDF files are allowed' });
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      setUploading(true);
      const res = await axios.post(`${API}/admin/upload-pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, pdf_url: res.data.url }));
      showMsg({ type: 'success', text: 'PDF uploaded' });
    } catch (e) {
      showMsg({ type: 'error', text: e.response?.data?.detail || 'PDF upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!form.title || !form.pdf_url) {
      showMsg({ type: 'error', text: 'Title and PDF are required' });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        title: form.title.trim(),
        category: form.category,
        month: form.month,
        year: parseInt(form.year, 10) || new Date().getFullYear(),
        pdf_url: form.pdf_url.trim(),
        tracker_url: form.tracker_url.trim() || null,
        description: form.description.trim() || null,
        is_active: !!form.is_active,
      };
      if (form.id) {
        await axios.patch(`${API}/admin/programs/${form.id}`, payload);
        showMsg({ type: 'success', text: 'Program updated' });
      } else {
        await axios.post(`${API}/admin/programs`, payload);
        showMsg({ type: 'success', text: 'Program created' });
      }
      setShowForm(false);
      setForm(emptyForm('training'));
      fetchAll();
    } catch (e) {
      showMsg({ type: 'error', text: e.response?.data?.detail || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (program) => {
    try {
      await axios.patch(`${API}/admin/programs/${program.id}`, { is_active: !program.is_active });
      fetchAll();
    } catch (e) {
      showMsg({ type: 'error', text: 'Toggle failed' });
    }
  };

  const handleDelete = async (program) => {
    if (!window.confirm(`Delete "${program.title}" (${program.month} ${program.year})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/programs/${program.id}`);
      showMsg({ type: 'success', text: 'Program deleted' });
      fetchAll();
    } catch (e) {
      showMsg({ type: 'error', text: 'Delete failed' });
    }
  };

  if (authLoading) return <div className="page-content"><p style={{ textAlign: 'center', padding: 60 }}>Loading…</p></div>;
  if (!isAuthenticated) {
    return <div className="page-content"><p style={{ textAlign: 'center', padding: 60 }}>Please <Link to="/admin/login">login</Link> to access this page.</p></div>;
  }

  const trainingPrograms = programs.filter(p => p.category === 'training');
  const nutritionPrograms = programs.filter(p => p.category === 'nutrition');

  return (
    <div className="page-content">
      <section style={{ padding: '32px var(--spacing-medium)' }}>
        <div className="container">
          <Link to="/admin/dashboard" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text-light)', textDecoration: 'none', marginBottom: 16 }}>
            <ArrowLeft size={16} /> Back to Dashboard
          </Link>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
            <div>
              <h1 className="display-large" style={{ margin: 0 }}>Training & Nutrition</h1>
              <p style={{ color: 'var(--text-light)', margin: '8px 0 0' }}>Upload and manage monthly programs for premium members</p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => handleStartCreate('training')} className="btn-primary" data-testid="new-training-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Dumbbell size={16} /> New Training Program
              </button>
              <button onClick={() => handleStartCreate('nutrition')} className="btn-secondary" data-testid="new-nutrition-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Apple size={16} /> New Nutrition Program
              </button>
            </div>
          </div>

          {message && (
            <div data-testid="programs-message" style={{
              padding: '12px 16px', marginBottom: 16, borderRadius: 8,
              background: message.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
              border: `1px solid ${message.type === 'error' ? '#ef4444' : '#22c55e'}`,
              color: message.type === 'error' ? '#fca5a5' : '#86efac'
            }}>{message.text}</div>
          )}

          {/* Form */}
          {showForm && (
            <div className="network-card" style={{ padding: 24, marginBottom: 24 }} data-testid="program-form">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 className="heading-2" style={{ margin: 0 }}>{form.id ? 'Edit' : 'Add'} {form.category === 'training' ? 'Training' : 'Nutrition'} Program</h2>
                <button onClick={() => { setShowForm(false); setForm(emptyForm('training')); }} style={{ background: 'none', border: 'none', color: 'var(--text-light)', cursor: 'pointer' }}><X size={20} /></button>
              </div>
              <form onSubmit={handleSave} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Title *</label>
                  <input data-testid="form-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. JR/SR Strength & Speed Program" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Category</label>
                  <select data-testid="form-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={inputStyle}>
                    <option value="training">Training</option>
                    <option value="nutrition">Nutrition</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Month</label>
                  <select data-testid="form-month" value={form.month} onChange={e => setForm({ ...form, month: e.target.value })} style={inputStyle}>
                    {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Year</label>
                  <input data-testid="form-year" type="number" min="2024" max="2050" value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} style={inputStyle} />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>PDF Document *</label>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input data-testid="form-pdf-url" value={form.pdf_url} onChange={e => setForm({ ...form, pdf_url: e.target.value })} placeholder="https://… (paste a URL or upload below)" style={{ ...inputStyle, flex: 1, minWidth: 240 }} />
                    <label className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0 }}>
                      <Upload size={16} /> {uploading ? 'Uploading…' : 'Upload PDF'}
                      <input data-testid="form-pdf-file" type="file" accept="application/pdf" onChange={e => handleUploadPdf(e.target.files?.[0])} style={{ display: 'none' }} disabled={uploading} />
                    </label>
                    {form.pdf_url && (
                      <a href={form.pdf_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-primary)', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
                        <ExternalLink size={14} /> Preview
                      </a>
                    )}
                  </div>
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Tracker URL (optional)</label>
                  <input data-testid="form-tracker-url" value={form.tracker_url} onChange={e => setForm({ ...form, tracker_url: e.target.value })} placeholder="https://docs.google.com/spreadsheets/d/…/copy" style={inputStyle} />
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-light)', margin: '4px 0 0' }}>Tip: end your Google Sheets share URL with <code>/copy</code> so members get their own private copy.</p>
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Description (optional)</label>
                  <textarea data-testid="form-description" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Short summary shown above the PDF" style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                <div>
                  <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input data-testid="form-is-active" type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                    Active (visible to premium members)
                  </label>
                </div>
                <div style={{ gridColumn: '1/-1', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={saving} data-testid="form-save-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Save size={16} /> {saving ? 'Saving…' : (form.id ? 'Update Program' : 'Create Program')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Lists */}
          {loading ? (
            <p style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)' }}>Loading programs…</p>
          ) : (
            <>
              <ProgramList title="Training Programs" icon={Dumbbell} accent="#ef4444" items={trainingPrograms} onEdit={handleEdit} onDelete={handleDelete} onToggle={handleToggleActive} emptyLabel="No training programs yet." />
              <ProgramList title="Nutrition Programs" icon={Apple} accent="#22c55e" items={nutritionPrograms} onEdit={handleEdit} onDelete={handleDelete} onToggle={handleToggleActive} emptyLabel="No nutrition programs yet." />
            </>
          )}
        </div>
      </section>
    </div>
  );
};

const ProgramList = ({ title, icon: Icon, accent, items, onEdit, onDelete, onToggle, emptyLabel }) => (
  <div style={{ marginBottom: 32 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <Icon size={22} style={{ color: accent }} />
      <h3 style={{ margin: 0 }}>{title}</h3>
      <span style={{ color: 'var(--text-light)', fontSize: '0.85rem' }}>({items.length})</span>
    </div>
    {items.length === 0 ? (
      <p style={{ color: 'var(--text-light)', fontStyle: 'italic' }}>{emptyLabel}</p>
    ) : (
      <div style={{ display: 'grid', gap: 12 }}>
        {items.map(p => (
          <div key={p.id} data-testid={`program-row-${p.id}`} className="network-card" style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, opacity: p.is_active ? 1 : 0.55 }}>
            <div style={{ minWidth: 240, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <strong>{p.title}</strong>
                <span style={{ padding: '2px 10px', borderRadius: 999, background: `${accent}22`, color: accent, fontSize: '0.7rem', fontWeight: 700 }}>{p.month} {p.year}</span>
                {!p.is_active && <span style={{ padding: '2px 10px', borderRadius: 999, background: 'rgba(100,100,100,0.3)', color: 'var(--text-light)', fontSize: '0.7rem', fontWeight: 700 }}>HIDDEN</span>}
              </div>
              {p.description && <p style={{ margin: '6px 0 0', color: 'var(--text-light)', fontSize: '0.85rem' }}>{p.description}</p>}
              <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap', fontSize: '0.8rem' }}>
                <a href={p.pdf_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--brand-primary)' }}><FileText size={14} /> View PDF</a>
                {p.tracker_url && <a href={p.tracker_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--brand-primary)' }}><ExternalLink size={14} /> Tracker</a>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => onToggle(p)} className="btn-secondary" title={p.is_active ? 'Hide from members' : 'Show to members'} data-testid={`toggle-${p.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {p.is_active ? <><EyeOff size={14} /> Hide</> : <><Eye size={14} /> Show</>}
              </button>
              <button onClick={() => onEdit(p)} className="btn-secondary" data-testid={`edit-${p.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Edit2 size={14} /> Edit
              </button>
              <button onClick={() => onDelete(p)} data-testid={`delete-${p.id}`} style={{ background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.35)', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const labelStyle = { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-light)', marginBottom: 6 };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-medium)', background: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)', fontSize: '0.95rem' };

export default AdminPrograms;
